// auth-store.js — session store (Brief 188 upgrade from the token-only stub).
//
// Brief 188 landed the real login round-trip (crm-client.js + kfe-login.js)
// against crm-staging, so this module now holds the full normalised session
// crmWebLogin() returns, not just a bare token. Sync-engine wiring (Brief
// 025's successor) and the device-switch UI (Brief 029) are separate briefs
// that will read/extend this store further.
//
// Storage stays in localStorage — no httpOnly cookie / native keychain
// equivalent exists on the web platform. Callers should depend only on the
// API below, not the storage detail.

// Imports crm-client for signOutFull()'s server-side invalidation. That makes
// auth-store <-> crm-client a module cycle (crm-client imports getSession here
// for the Bearer header). It is safe: neither module reads the other's
// bindings at evaluation time, and every cross-module binding is a hoisted
// `export function`, so whichever side evaluates first sees the other's
// functions already initialised.
import { crmLogout } from "./crm-client.js";

const SESSION_KEY = "kfe_auth_session";
// Pre-Brief-188 storage key. Read as a fallback so a caller that only ever
// used setToken() (never setSession()) still works; cleared the first time
// setSession() runs so the two representations can't drift.
const LEGACY_TOKEN_KEY = "kfe_auth_token";

/**
 * @typedef {object} AuthSessionUser
 * @property {string|number} [id]
 * @property {string} [username]
 * @property {string} [name]
 * @property {string} [licenseNum]
 * @property {string} [licenseEndDate]
 */
/**
 * @typedef {object} AuthSessionInfo
 * @property {string|number} [id]
 * @property {string} [clientType]
 */
/**
 * @typedef {object} AuthSessionDevice
 * @property {string|number} [customerDeviceId]
 * @property {string} [uaid]
 * @property {string} [deviceModel]
 */
/**
 * @typedef {object} AuthSession
 * @property {string} token
 * @property {string} [tokenType]
 * @property {number} [expiresIn] seconds (e.g. 7200)
 * @property {number} issuedAt client-clock ms, stamped by setSession()
 * @property {AuthSessionUser} [user]
 * @property {AuthSessionInfo} [session]
 * @property {AuthSessionDevice} [device]
 */

/** @returns {AuthSession | null} */
export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Persist the normalised crmWebLogin() payload as the active session. Stamps
 * `issuedAt` with the client clock so a future refresh can compare against
 * `expiresIn` (7200s) — no refresh logic lands in this brief, just the field.
 * @param {Omit<AuthSession, "issuedAt"> & { issuedAt?: number }} session
 */
export function setSession(session) {
  const withIssuedAt = { ...session, issuedAt: session.issuedAt ?? Date.now() };
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(withIssuedAt));
    // Drop the legacy key so getToken() has exactly one source of truth going
    // forward — see LEGACY_TOKEN_KEY docblock above.
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* private mode etc. — no-op */ }
}

/** @returns {string | null} */
export function getToken() {
  const session = getSession();
  if (session?.token) return session.token;
  try { return localStorage.getItem(LEGACY_TOKEN_KEY); }
  catch { return null; }
}

/** @returns {boolean} */
export function isSignedIn() {
  return getToken() !== null;
}

/**
 * Legacy setter, preserved for API compatibility. Prefer setSession() for
 * any new caller — it carries the full user/session/device payload.
 * @param {string} token
 */
export function setToken(token) {
  try { localStorage.setItem(LEGACY_TOKEN_KEY, token); }
  catch { /* private mode etc. — no-op */ }
}

/**
 * Clear only the session. Per Brief 014 (still the contract): signing out
 * must NOT wipe learner data (mmuStreaks, itemProgress, settings) — that's
 * tied to learnerId and gets re-fetched on next sign-in. Just drop the
 * credential (both the current session key and the legacy token key).
 */
export function clearToken() {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* no-op */ }
}

/**
 * Sign out LOCALLY ONLY: clear session, announce it, and let the caller route
 * to /login. No server call.
 *
 * This stays the right entry point for a session that the server has ALREADY
 * invalidated — crm-client's onSessionInvalid listeners (401, or 409/91049)
 * fire on exactly that case, and calling crmLogout() there would only earn a
 * second 401. Learner-initiated sign-out should call signOutFull() instead.
 */
export function signOut() {
  clearToken();
  document.dispatchEvent(new Event("kfe:auth-changed"));
}

/**
 * Sign out of BOTH the server (DELETE /app/auth/logout invalidates the JWT
 * server-side) and the local session (clearToken + kfe:auth-changed). This is
 * the entry point for every learner-initiated sign-out (Settings row, header
 * menu). Brief 191.
 *
 * Order is load-bearing: crmLogout() MUST run BEFORE clearToken(), because
 * crm-client reads the Bearer out of this store. Clear first and crmLogout()
 * short-circuits to { kind: "session_displaced" } with no network call at
 * all, leaving the server session live for the rest of the JWT TTL (2h) —
 * the phantom-session accumulation Brief 191 exists to stop.
 *
 * Server failures are non-fatal: local state is cleared regardless. Leaving
 * the learner stuck signed-in locally because the server didn't answer is
 * worse than orphaning a session that expires in at most two hours. The
 * failure is logged, never rethrown, and never retried.
 *
 * Mirrors iOS's order (KFEDeactivateDeviceMgr.m: server round-trip first,
 * local state flipped only after). iOS refuses to flip local state on a
 * server failure — correct there, because iOS's operation is an irreversible
 * device deactivation, not a session logout. See Brief 191 for the reasoning
 * behind the web divergence.
 *
 * @returns {Promise<{ serverInvalidated: boolean }>}
 */
export async function signOutFull() {
  // Brief 025: give the sync engine one best-effort chance to ship the outbox
  // before the credential goes away — after this returns, crm-client has no
  // Bearer to attach and every push short-circuits. Deliberately NOT awaited
  // past its own listener: Brief 191's principle is that sign-out never blocks
  // on a server call, and an unflushed row is not lost — it stays in the durable
  // outbox and ships on the next login.
  try {
    document.dispatchEvent(new Event("kfe:sync-teardown"));
  } catch { /* DOM-free context — nothing to notify */ }

  let serverInvalidated = false;
  try {
    const res = await crmLogout();
    serverInvalidated = res.success === true;
    if (!res.success) {
      console.warn("[auth-store] crmLogout failed — clearing local session anyway", res.error);
    }
  } catch (err) {
    console.warn("[auth-store] crmLogout threw — clearing local session anyway", err);
  }
  signOut();
  return { serverInvalidated };
}
